import { useCallback, useRef } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import IconButton from 'LuxKit/Button/IconButton'
import noop from 'lib/function/noop'
import LockedOptionsList from './LockedOptionsList'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'

export interface CardOption {
  title: string
  description?: string
  emoji?: string
  placeSearch?: string
  action?: string
}

export type CardCategory = 'generic' | 'places' | 'locations'

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories?: CardCategory
  onInfoClick?: (option: CardOption, category: CardCategory) => void
  onAction?: (action: string) => boolean | void
}

function CardsList({ options, onSelectionChange, select, disabled = false, selection, categories, onInfoClick = noop, onAction }: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
    onAction,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    const option = options[index]
    if (option && categories) {
      onInfoClickRef.current(option, categories)
    }
  }, [options, categories])

  const getOptionKey = useCallback((option: CardOption, index: number) => `${option.title}-${index}`, [])

  const renderOption = useCallback(({ option, index, isSelected }: { option: CardOption; index: number; isSelected: boolean }) => (
    <SelectableCard
      role={select === 'single' ? 'radio' : 'checkbox'}
      aria-checked={isSelected}
      disabled={disabled}
      onClick={optionClickHandlers[index]}
      className={clsx({ selected: isSelected })}
    >
      <Group direction="horizontal" gap={16} fullWidth verticalAlign="center">
        {option.emoji && (
          <BodyText variant="large" colour="neutral-one" weight="bold">
            {option.emoji}
          </BodyText>
        )}
        <Group direction="vertical" gap={2} fullWidth>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium" colour="neutral-one" weight="bold">
              {option.title}
            </BodyText>
            {(categories === 'locations' || categories === 'places') && (
              <IconButton
                kind="tertiary"
                size="small"
                data-option-index={index}
                onClick={handleInfoClick}
              >
                <LineInfoCircleIcon />
              </IconButton>
            )}
          </Group>
          {option.description && (
            <BodyText variant="medium" colour="neutral-two">
              {option.description}
            </BodyText>
          )}
        </Group>
        {select === 'multiple' && (
          <CircleCheckboxInput
            checked={isSelected}
            disabled={disabled}
            aria-hidden="true"
          />
        )}
        {(option.action === 'call_expert' || option.action === 'schedule_callback') && (
          <LineArrowUpRightIcon />
        )}
      </Group>
    </SelectableCard>
  ), [categories, disabled, handleInfoClick, optionClickHandlers, select])

  return (
    <LockedOptionsList
      options={options}
      disabled={disabled}
      selectedIndices={selectedIndices}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
    />
  )
}

export default CardsList
